import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Key, Building2, Cpu, Users, BarChart3, Globe, ChevronDown,
  ChevronRight, Check, X, Zap, Shield, Smartphone, Settings,
  FileText, Menu, Loader2, Lock, ArrowRight, Clock,
  Star, Database, Wifi, Bell, Calendar,
  Mail, ExternalLink, Hotel,
  Sun, Moon, ShieldCheck, Eye, LogIn,
} from 'lucide-react';

import { T, BRANDS_ALL, FAQS, PLANS_DATA } from './landing/translations';
import { ShowcaseSection } from './landing/UIShowcase';
import { DInput, DemoSuccess } from './landing/DemoSection';
import { LoginModal } from './landing/LoginModal';
import { useTheme } from '../contexts/ThemeContext';
import { SeoHead } from '../components/SeoHead';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FEATURES_LIST = [
  { icon: Key,        key: 'f1', accent: '#c9a84c' },
  { icon: Cpu,        key: 'f2', accent: '#7c3aed' },
  { icon: FileText,   key: 'f3', accent: '#0891b2' },
  { icon: Smartphone, key: 'f4', accent: '#059669' },
  { icon: Users,      key: 'f5', accent: '#dc2626' },
  { icon: BarChart3,  key: 'f6', accent: '#c9a84c' },
  { icon: Globe,      key: 'f7', accent: '#7c3aed' },
  { icon: Zap,        key: 'f8', accent: '#0891b2' },
];

const FOR_WHO_ICONS = [Hotel, Building2, Users, Globe];
const TEAM_ICONS = [Eye, Settings, FileText, Smartphone];
const SECURITY_ICONS = [BarChart3, ShieldCheck, Key, Lock, Bell, LogIn];

export default function LandingPage() {
  const [lang, setLang] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('lang') === 'en' ? 'en' : 'it';
  });
  const [openFaq, setOpenFaq] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', company: '', email: '', phone: '', plan: 'professional' });
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [demoResult, setDemoResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [stickyCTADismissed, setStickyCTADismissed] = useState(false);
  const t = T[lang];
  const { theme, toggleTheme } = useTheme();

  const toggleLang = () => {
    const newLang = lang === 'it' ? 'en' : 'it';
    setLang(newLang);
    const newSearch = newLang === 'en' ? '?lang=en' : '';
    window.history.replaceState({}, '', `/${newSearch}`);
  };

  const navBg    = theme === 'dark' ? 'rgba(8,8,8,0.92)'    : 'rgba(250,248,245,0.95)';
  const menuBg   = theme === 'dark' ? 'rgba(8,8,8,0.98)'    : 'rgba(250,248,245,0.98)';
  const stickyBg = theme === 'dark' ? 'rgba(8,8,8,0.97)'    : 'rgba(250,248,245,0.97)';
  const stickyGrad = theme === 'dark'
    ? 'linear-gradient(to bottom, transparent, rgba(8,8,8,0.96))'
    : 'linear-gradient(to bottom, transparent, rgba(250,248,245,0.96))';

  const setDf = (k, v) => setDemoForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    let lastValue = false;
    const checkAndUpdate = () => {
      if (stickyCTADismissed) return;
      const cookieHandled = !!localStorage.getItem('ostivex_cookie_consent');
      const heroH = window.innerHeight * 0.85;
      const demoEl = document.getElementById('demo');
      const demoTop = demoEl ? demoEl.getBoundingClientRect().top : Infinity;
      const next = cookieHandled && window.scrollY > heroH && demoTop > 80;
      if (next !== lastValue) { lastValue = next; setShowStickyCTA(next); }
    };
    window.addEventListener('scroll', checkAndUpdate, { passive: true });
    const timer = setInterval(() => {
      const cookieHandled = !!localStorage.getItem('ostivex_cookie_consent');
      if (cookieHandled && window.scrollY > window.innerHeight * 0.85 && !stickyCTADismissed) {
        setShowStickyCTA(true);
      }
    }, 3000);
    return () => { window.removeEventListener('scroll', checkAndUpdate); clearInterval(timer); };
  }, [stickyCTADismissed]);

  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); setMenuOpen(false); };

  // Scroll-reveal: aggiunge .in-view agli elementi .reveal quando entrano nel viewport
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(en => {
        if (en.isIntersecting) { en.target.classList.add('in-view'); obs.unobserve(en.target); }
      });
    }, { threshold: 0.12 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [lang]);

  const submitDemo = async (e) => {
    e.preventDefault();
    setDemoError('');
    if (!demoForm.name.trim()) return setDemoError(lang === 'it' ? 'Inserisci il tuo nome.' : 'Please enter your name.');
    if (!demoForm.company.trim()) return setDemoError(lang === 'it' ? 'Inserisci il nome della struttura.' : 'Please enter the property name.');
    if (!demoForm.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(demoForm.email)) return setDemoError(lang === 'it' ? 'Inserisci un\'email valida.' : 'Please enter a valid email.');
    setDemoLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/demo/request`, { ...demoForm, lang });
      setDemoResult(data);
    } catch (err) {
      setDemoError(err.response?.data?.detail || (lang === 'it' ? 'Errore. Riprova tra qualche istante.' : 'Error. Please retry in a moment.'));
    } finally {
      setDemoLoading(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); });
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--lux-bg)', color: 'var(--lux-text)', fontFamily: "'DM Sans', sans-serif" }}>
      <SeoHead page="home" lang={lang} theme={theme} />

      {/* ── NAV ─────────────────────────────────────── */}
      <nav style={{ background: navBg, backdropFilter: 'blur(24px)', borderBottom: '1px solid var(--lux-border-1)' }}
        className="fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-sm flex items-center justify-center shrink-0" style={{ background: 'var(--lux-gold)' }}>
              <Key size={12} color="#000" strokeWidth={2.5} />
            </div>
            <span className="font-serif text-lg sm:text-xl font-light tracking-wide">Shangrilux</span>
          </div>

          <div className="hidden lg:flex items-center gap-8">
            {[
              ['features',      t.nav_features],
              ['for-who',       lang === 'it' ? 'Per chi è' : 'Who it\'s for'],
              ['integrations',  t.nav_integrations],
              ['pricing',       t.nav_pricing],
              ['faq',           t.nav_faq],
            ].map(([s, label]) => (
              <button key={s} onClick={() => scrollTo(s)} className="text-sm transition-colors"
                style={{ color: 'var(--lux-text-muted)' }}
                onMouseEnter={e => e.target.style.color = 'var(--lux-gold)'}
                onMouseLeave={e => e.target.style.color = 'var(--lux-text-muted)'}>
                {label}
              </button>
            ))}
            <a href="/blog" className="text-sm transition-colors"
              style={{ color: 'var(--lux-text-muted)' }}
              onMouseEnter={e => e.target.style.color = 'var(--lux-gold)'}
              onMouseLeave={e => e.target.style.color = 'var(--lux-text-muted)'}>
              {lang === 'it' ? 'Guide' : 'Guides'}
            </a>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={toggleLang}
              className="text-xs px-2.5 sm:px-3 py-1.5 rounded-full shrink-0 transition-colors"
              style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
              {lang === 'it' ? 'EN' : 'IT'}
            </button>
            <button onClick={toggleTheme} data-testid="theme-toggle-btn"
              className="p-1.5 rounded-full transition-colors shrink-0"
              style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}
              aria-label={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}>
              {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <button onClick={() => setShowLogin(true)}
              className="hidden md:flex items-center gap-1.5 text-xs px-3 py-2 transition-colors"
              style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}
              data-testid="nav-login-btn">
              <Lock size={12} /> {t.nav_login}
            </button>
            <button onClick={() => scrollTo('demo')}
              className="btn-metallic hidden sm:block text-xs px-4 sm:px-5 py-2.5 font-semibold tracking-wide whitespace-nowrap uppercase"
              data-testid="nav-demo-btn">
              {lang === 'it' ? 'Demo Gratuita' : 'Free Demo'}
            </button>
            <button className="lg:hidden p-1.5" onClick={() => setMenuOpen(o => !o)}>
              <Menu size={20} style={{ color: 'var(--lux-text-muted)' }} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden px-4 sm:px-6 pb-5 flex flex-col gap-1" style={{ borderTop: '1px solid var(--lux-border-1)', background: menuBg }}>
            {[
              ['features',     t.nav_features],
              ['for-who',      lang === 'it' ? 'Per chi è' : 'Who it\'s for'],
              ['integrations', t.nav_integrations],
              ['pricing',      t.nav_pricing],
              ['faq',          t.nav_faq],
            ].map(([s, label]) => (
              <button key={s} onClick={() => scrollTo(s)} className="text-left text-sm py-2.5" style={{ color: 'var(--lux-text-muted)' }}>{label}</button>
            ))}
            <a href="/blog" className="text-left text-sm py-2.5" style={{ color: 'var(--lux-text-muted)' }}>
              {lang === 'it' ? 'Guide' : 'Guides'}
            </a>
            <button onClick={() => { setShowLogin(true); setMenuOpen(false); }} className="text-left text-sm py-2.5" style={{ color: 'var(--lux-text-muted)' }}>{t.nav_login}</button>
            <button onClick={toggleTheme} className="flex items-center gap-2 text-left text-sm py-2.5" style={{ color: 'var(--lux-text-muted)' }}>
              {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
              {theme === 'dark' ? (lang === 'it' ? 'Tema chiaro' : 'Light mode') : (lang === 'it' ? 'Tema scuro' : 'Dark mode')}
            </button>
            <button onClick={() => { scrollTo('demo'); setMenuOpen(false); }}
              className="mt-3 w-full py-3.5 text-sm font-semibold uppercase tracking-wide"
              style={{ background: 'var(--lux-gold)', color: '#000' }}>
              {t.nav_demo}
            </button>
          </div>
        )}
      </nav>

      {/* ── HERO ─────────────────────────────────────── */}
      <section className="min-h-screen flex flex-col items-center justify-center text-center px-5 sm:px-6 pt-20 sm:pt-24 pb-12 sm:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 hero-kenburns" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&q=80&w=2800)',
          backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: theme === 'dark' ? 0.38 : 0.25,
        }} />
        <div className="absolute inset-0" style={{
          background: theme === 'dark'
            ? 'linear-gradient(to bottom, rgba(8,8,8,0.78) 0%, rgba(8,8,8,0.5) 45%, #080808 96%)'
            : 'linear-gradient(to bottom, rgba(250,248,245,0.88) 0%, rgba(250,248,245,0.72) 45%, #faf8f5 96%)',
        }} />
        <div className="absolute inset-0 hero-noise" />
        <div className="absolute inset-0 opacity-[0.022]"
          style={{ backgroundImage: 'linear-gradient(var(--lux-gold) 1px, transparent 1px), linear-gradient(90deg, var(--lux-gold) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] sm:w-[900px] h-[600px] sm:h-[900px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(201,168,76,0.07) 0%, transparent 65%)' }} />

        <div className="relative z-10 max-w-5xl mx-auto w-full">
          <div className="hero-anim hero-anim-1 inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-xs backdrop-blur-md"
            style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: 'var(--lux-gold)' }}>
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" /> {t.hero_eyebrow}
          </div>

          <h1 className="hero-anim hero-anim-2 font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light leading-[1.05] mb-6" style={{ color: 'var(--lux-text)' }}>
            {t.hero_title.split('\n').map((line, i) => (
              <span key={i}>{line}{i < t.hero_title.split('\n').length - 1 && <br />}</span>
            ))}
            <br /><span className="gold-gradient-text italic">{t.hero_title_gold}</span>
          </h1>

          <p className="hero-anim hero-anim-3 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed mb-10 px-2" style={{ color: 'var(--lux-text-sec)' }}>
            {t.hero_sub}
          </p>

          <div className="hero-anim hero-anim-4 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-14 px-4 sm:px-0">
            <button onClick={() => scrollTo('demo')}
              className="btn-metallic px-8 py-4 font-semibold text-sm flex items-center gap-2 justify-center tracking-widest uppercase"
              data-testid="hero-cta-btn">
              {t.hero_cta} <ChevronRight size={15} />
            </button>
            <button onClick={() => scrollTo('pricing')}
              className="btn-lux-outline px-8 py-4 text-sm tracking-widest uppercase"
              data-testid="hero-secondary-btn">
              {t.hero_cta2}
            </button>
          </div>

          <div className="hero-anim hero-anim-5 grid grid-cols-2 sm:grid-cols-4 w-full" style={{ border: '1px solid var(--lux-border-1)', background: 'var(--lux-border-1)', gap: '1px' }}>
            {[
              { v: t.hero_stat1, s: t.hero_stat1_sub },
              { v: t.hero_stat2, s: t.hero_stat2_sub },
              { v: t.hero_stat3, s: t.hero_stat3_sub },
              { v: t.hero_stat4, s: t.hero_stat4_sub },
            ].map((st, i) => (
              <div key={i} className="py-5 px-4 text-center backdrop-blur-md" style={{ background: theme === 'dark' ? 'rgba(13,13,13,0.75)' : 'rgba(242,237,228,0.78)' }}>
                <p className="font-serif text-2xl sm:text-3xl font-light" style={{ color: 'var(--lux-gold)' }}>{st.v}</p>
                <p className="text-[10px] uppercase tracking-widest mt-1.5" style={{ color: 'var(--lux-text-muted-2)' }}>{st.s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROBLEM ─────────────────────────────────── */}
      <section className="py-20 md:py-28 px-5 sm:px-6" style={{ background: 'var(--lux-surface-1)', borderTop: '1px solid var(--lux-border-1)', borderBottom: '1px solid var(--lux-border-1)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="reveal text-center mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.4em] mb-4" style={{ color: '#ef4444' }}>{t.problem_label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mb-5" style={{ color: 'var(--lux-text)' }}>
              {t.problem_title.split('\n').map((l, i) => <span key={i}>{l}{i < t.problem_title.split('\n').length - 1 && <br />}</span>)}
            </h2>
            <p className="text-sm sm:text-base max-w-xl mx-auto leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t.problem_sub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              { icon: Key,  t: t.problem1_t, d: t.problem1_d },
              { icon: Wifi, t: t.problem2_t, d: t.problem2_d },
              { icon: Bell, t: t.problem3_t, d: t.problem3_d },
            ].map(({ icon: Icon, t: title, d: desc }, i) => (
              <div key={i} className="p-6 rounded-sm" style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.15)' }}>
                <Icon size={20} className="mb-4" style={{ color: '#ef4444' }} strokeWidth={1.5} />
                <h3 className="font-semibold mb-2 text-sm" style={{ color: 'var(--lux-text)' }}>{title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{desc}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-12">
            <div className="flex flex-col items-center gap-2">
              <p className="text-xs uppercase tracking-[0.3em]" style={{ color: 'var(--lux-gold)' }}>{t.solution_label}</p>
              <div className="w-px h-12" style={{ background: 'linear-gradient(to bottom, var(--lux-gold), transparent)' }} />
            </div>
          </div>
          <p className="text-center font-serif text-2xl sm:text-3xl md:text-4xl font-light mt-4 px-2" style={{ color: 'var(--lux-text)' }}>
            {t.solution_title.split('\n').map((l, i) => <span key={i}>{l}{i < t.solution_title.split('\n').length - 1 && <br />}</span>)}
          </p>
        </div>
      </section>

      {/* ── FEATURES ────────────────────────────────── */}
      <section id="features" className="py-20 md:py-28 px-5 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="reveal text-center mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--lux-gold)' }}>Features</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light" style={{ color: 'var(--lux-text)' }}>
              {t.feat_title}<br /><span style={{ color: 'var(--lux-gold)' }}>{t.feat_title2}</span>
            </h2>
            <p className="text-sm mt-4 max-w-2xl mx-auto leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t.feat_sub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {FEATURES_LIST.map(({ icon: Icon, key, accent }) => (
              <div key={key} className="card-lux p-6 rounded-sm group"
                style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
                <div className="w-9 h-9 rounded-sm flex items-center justify-center mb-5" style={{ background: `${accent}18` }}>
                  <Icon size={16} style={{ color: accent }} strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-semibold mb-2.5" style={{ color: 'var(--lux-text)' }}>{t[`${key}_t`]}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t[`${key}_d`]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOR WHO ─────────────────────────────────── */}
      <section id="for-who" className="py-20 md:py-28 px-5 sm:px-6" style={{ background: 'var(--lux-surface-1)', borderTop: '1px solid var(--lux-border-1)', borderBottom: '1px solid var(--lux-border-1)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="reveal text-center mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--lux-gold)' }}>{t.for_who_label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mb-5" style={{ color: 'var(--lux-text)' }}>
              {t.for_who_title.split('\n').map((l, i) => <span key={i}>{l}{i < t.for_who_title.split('\n').length - 1 && <br />}</span>)}
            </h2>
            <p className="text-sm max-w-xl mx-auto leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t.for_who_sub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[1, 2, 3, 4].map((n, i) => {
              const Icon = FOR_WHO_ICONS[i];
              return (
                <div key={n} className="card-lux p-6 rounded-sm" style={{ background: 'var(--lux-bg)', border: '1px solid var(--lux-border-1)' }}>
                  <div className="mb-4">
                    <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--lux-gold)', border: '1px solid rgba(201,168,76,0.2)' }}>
                      {t[`for_who_${n}_tag`]}
                    </span>
                  </div>
                  <Icon size={18} className="mb-3" style={{ color: 'var(--lux-gold)' }} strokeWidth={1.5} />
                  <h3 className="font-semibold text-sm mb-2" style={{ color: 'var(--lux-text)' }}>{t[`for_who_${n}_title`]}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t[`for_who_${n}_desc`]}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── PRODUCT SHOWCASE ────────────────────────── */}
      <ShowcaseSection lang={lang} />

      {/* ── INTEGRATIONS ────────────────────────────── */}
      <section id="integrations" className="py-20 md:py-28 px-5 sm:px-6" style={{ background: 'var(--lux-surface-1)', borderTop: '1px solid var(--lux-border-1)', borderBottom: '1px solid var(--lux-border-1)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="reveal text-center mb-10 md:mb-12">
            <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--lux-gold)' }}>{t.integrations_label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-light mb-3" style={{ color: 'var(--lux-text)' }}>
              {t.integrations_title.split('\n').map((l, i) => <span key={i}>{l}{i < t.integrations_title.split('\n').length - 1 && <br />}</span>)}
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t.integrations_sub}</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 sm:gap-3">
            {BRANDS_ALL.map(({ name, cat }) => (
              <div key={name} className="p-3 text-center rounded-sm transition-all"
                style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', transition: 'border-color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(201,168,76,0.5)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--lux-border-2)'}>
                <p className="text-xs font-semibold mb-0.5 leading-tight" style={{ color: 'var(--lux-text)' }}>{name}</p>
                <p className="text-[9px] sm:text-[10px] leading-tight" style={{ color: 'var(--lux-text-muted-3)' }}>{cat}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a href="/marketplace"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest transition-opacity hover:opacity-70"
              style={{ color: 'var(--lux-gold)' }}>
              {t.integrations_cta} <ChevronRight size={13} />
            </a>
          </div>
        </div>
      </section>

      {/* ── TEAM ROLES ──────────────────────────────── */}
      <section className="py-20 md:py-28 px-5 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="reveal text-center mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--lux-gold)' }}>{t.team_label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mb-5" style={{ color: 'var(--lux-text)' }}>
              {t.team_title.split('\n').map((l, i) => <span key={i}>{l}{i < t.team_title.split('\n').length - 1 && <br />}</span>)}
            </h2>
            <p className="text-sm max-w-xl mx-auto leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t.team_sub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[1, 2, 3, 4].map((n, i) => {
              const Icon = TEAM_ICONS[i];
              const colors = ['#c9a84c', '#7c3aed', '#0891b2', '#059669'];
              return (
                <div key={n} className="card-lux p-6 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
                  <div className="w-9 h-9 rounded-sm flex items-center justify-center mb-4" style={{ background: `${colors[i]}15` }}>
                    <Icon size={16} style={{ color: colors[i] }} strokeWidth={1.5} />
                  </div>
                  <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: colors[i] }}>{t[`team_${n}_role`]}</p>
                  <h3 className="font-semibold text-sm mb-2.5" style={{ color: 'var(--lux-text)' }}>{t[`team_${n}_title`]}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t[`team_${n}_desc`]}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── WHY OSTIVEX (Comparison) ─────────────────── */}
      <section className="py-20 md:py-28 px-5 sm:px-6" style={{ background: 'var(--lux-surface-1)', borderTop: '1px solid var(--lux-border-1)', borderBottom: '1px solid var(--lux-border-1)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="reveal text-center mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--lux-gold)' }}>{t.why_label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light" style={{ color: 'var(--lux-text)' }}>
              {t.why_title.split('\n').map((l, i) => <span key={i}>{l}{i < t.why_title.split('\n').length - 1 && <br />}</span>)}
            </h2>
          </div>
          <div className="overflow-x-auto -mx-5 sm:mx-0 px-5 sm:px-0">
            <div className="rounded-sm overflow-hidden min-w-[340px]" style={{ border: '1px solid var(--lux-border-1)' }}>
              <div className="grid grid-cols-3" style={{ background: 'var(--lux-surface-3)', borderBottom: '1px solid var(--lux-border-1)' }}>
                <div className="px-5 sm:px-6 py-4 text-xs uppercase tracking-widest" style={{ color: 'var(--lux-text-muted)' }}>{t.compare_feature}</div>
                <div className="px-5 sm:px-6 py-4 text-xs uppercase tracking-widest text-center" style={{ color: 'var(--lux-text-muted)' }}>{t.compare_other}</div>
                <div className="px-5 sm:px-6 py-4 text-xs uppercase tracking-widest text-center" style={{ color: 'var(--lux-gold)' }}>{t.compare_ostivex}</div>
              </div>
              {t.compare_rows_it.map(([feat, , ], i) => (
                <div key={i} className="grid grid-cols-3"
                  style={{ borderBottom: i < t.compare_rows_it.length - 1 ? '1px solid var(--lux-border-1)' : 'none', background: i % 2 === 0 ? 'var(--lux-bg)' : 'var(--lux-surface-1)' }}>
                  <div className="px-5 sm:px-6 py-3.5 text-xs sm:text-sm" style={{ color: 'var(--lux-text-sec)' }}>{feat}</div>
                  <div className="px-5 sm:px-6 py-3.5 flex justify-center items-center"><X size={14} style={{ color: '#4b5563' }} /></div>
                  <div className="px-5 sm:px-6 py-3.5 flex justify-center items-center"><Check size={14} style={{ color: 'var(--lux-gold)' }} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────── */}
      <section className="py-20 md:py-28 px-5 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="reveal text-center mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--lux-gold)' }}>{t.how_label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light" style={{ color: 'var(--lux-text)' }}>{t.how_title}</h2>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute h-px"
              style={{ top: '28px', left: 'calc(16.66% + 4px)', right: 'calc(16.66% + 4px)', background: 'linear-gradient(to right, transparent, rgba(201,168,76,0.4) 20%, rgba(201,168,76,0.4) 80%, transparent)', zIndex: 0 }} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
              {[
                { tk: 'how1_t', dk: 'how1_d', icon: Settings },
                { tk: 'how2_t', dk: 'how2_d', icon: Database },
                { tk: 'how3_t', dk: 'how3_d', icon: Zap },
              ].map(({ tk, dk, icon: Icon }, i) => (
                <div key={i} className="relative flex gap-5 md:block" style={{ zIndex: 1 }}>
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-sm flex items-center justify-center shrink-0 mb-0 md:mb-6"
                    style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-gold)' }}>
                    <Icon size={20} style={{ color: 'var(--lux-gold)' }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2 md:mb-3 text-sm" style={{ color: 'var(--lux-text)' }}>{t[tk]}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t[dk]}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ────────────────────────────── */}
      <section className="py-20 md:py-28 px-5 sm:px-6" style={{ background: 'var(--lux-surface-1)', borderTop: '1px solid var(--lux-border-1)', borderBottom: '1px solid var(--lux-border-1)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="reveal text-center mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--lux-gold)' }}>{t.testimonials_label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-light" style={{ color: 'var(--lux-text)' }}>{t.testimonials_title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {t.testimonials.map((testimonial, idx) => (
              <div key={idx} className="card-lux flex flex-col p-6 sm:p-7 rounded-sm"
                style={{ background: 'var(--lux-bg)', border: '1px solid var(--lux-border-1)' }}>
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: testimonial.stars }).map((_, i) => (
                    <Star key={i} size={12} fill="var(--lux-gold)" style={{ color: 'var(--lux-gold)' }} />
                  ))}
                </div>
                {/* Metric badge */}
                <span className="inline-flex self-start text-[9px] px-2.5 py-1 rounded-full font-semibold uppercase tracking-wider mb-4"
                  style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--lux-gold)', border: '1px solid rgba(201,168,76,0.2)' }}>
                  {testimonial.metric}
                </span>
                {/* Quote */}
                <p className="text-sm leading-relaxed flex-1 mb-5" style={{ color: 'var(--lux-text-sec)', fontStyle: 'italic' }}>
                  "{testimonial.text}"
                </p>
                {/* Author */}
                <div className="flex items-center gap-3 pt-5" style={{ borderTop: '1px solid var(--lux-border-1)' }}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold"
                    style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--lux-gold)', border: '1px solid rgba(201,168,76,0.25)' }}>
                    {testimonial.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: 'var(--lux-text)' }}>{testimonial.name}</p>
                    <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--lux-text-muted)' }}>{testimonial.role}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted-3)' }}>{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECURITY ────────────────────────────────── */}
      <section className="py-20 md:py-28 px-5 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="reveal text-center mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--lux-gold)' }}>{t.security_label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mb-5" style={{ color: 'var(--lux-text)' }}>
              {t.security_title.split('\n').map((l, i) => <span key={i}>{l}{i < t.security_title.split('\n').length - 1 && <br />}</span>)}
            </h2>
            <p className="text-sm max-w-xl mx-auto leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t.security_sub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {[1, 2, 3, 4, 5, 6].map((n, i) => {
              const Icon = SECURITY_ICONS[i];
              return (
                <div key={n} className="card-lux p-6 rounded-sm flex gap-4" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
                  <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.15)' }}>
                    <Icon size={15} style={{ color: 'var(--lux-gold)' }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm mb-1.5" style={{ color: 'var(--lux-text)' }}>{t[`security_${n}_t`]}</h3>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t[`security_${n}_d`]}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────── */}
      <section id="pricing" className="py-20 md:py-28 px-5 sm:px-6" style={{ background: 'var(--lux-surface-1)', borderTop: '1px solid var(--lux-border-1)', borderBottom: '1px solid var(--lux-border-1)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="reveal text-center mb-14 md:mb-20">
            <p className="text-xs uppercase tracking-[0.35em] mb-5" style={{ color: 'var(--lux-gold)' }}>{t.pricing_label}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight" style={{ color: 'var(--lux-text)' }}>
              {t.pricing_title}<br /><span style={{ color: 'var(--lux-gold)' }}>{t.pricing_title2}</span>
            </h2>
            <p className="text-sm mt-5 max-w-md mx-auto leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>{t.pricing_sub}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-4 lg:gap-6">
            {PLANS_DATA.map((plan, idx) => {
              const feats   = lang === 'it' ? plan.features_it : plan.features_en;
              const missing = lang === 'it' ? plan.missing_it  : plan.missing_en;
              return (
                <div key={plan.key}
                  className={`price-card flex flex-col rounded-sm relative ${plan.popular ? 'price-card-popular' : ''}`}
                  style={{
                    background: plan.popular ? 'var(--lux-surface-3)' : 'var(--lux-bg)',
                    border: plan.popular ? '1px solid var(--lux-gold)' : '1px solid var(--lux-border-1)',
                  }}>

                  {/* Gold accent top bar for popular card */}
                  {plan.popular && (
                    <div className="h-[3px] w-full rounded-t-sm" style={{ background: 'linear-gradient(90deg, #f3e3b6, #c9a84c 50%, #8c6d23)' }} />
                  )}

                  <div className="px-7 py-7 flex flex-col flex-1">

                    {/* ① Badge + Plan name row */}
                    <div className="mb-1">
                      {plan.popular ? (
                        <span className="inline-block px-3 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] mb-3"
                          style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--lux-gold)', border: '1px solid rgba(201,168,76,0.3)' }}>
                          {t.pricing_popular}
                        </span>
                      ) : (
                        <div className="mb-3 h-[22px]" /> /* spacer to align plan names */
                      )}
                      <h3 className="font-serif text-2xl sm:text-3xl font-light" style={{ color: 'var(--lux-text)' }}>
                        {t[`plan${idx + 1}_name`]}
                      </h3>
                    </div>

                    {/* ② Target description */}
                    <p className="text-xs leading-relaxed mt-2 mb-6" style={{ color: 'var(--lux-text-muted)', minHeight: '3em' }}>
                      {t[`plan${idx + 1}_desc`]}
                    </p>

                    {/* ③ Price block */}
                    <div className="pb-6" style={{ borderBottom: '1px solid var(--lux-border-1)' }}>
                      <div className="flex items-end gap-2">
                        <span className="font-serif text-5xl font-light leading-none" style={{ color: 'var(--lux-text)' }}>
                          €{plan.priceMonthly}
                        </span>
                        <div className="pb-1">
                          <p className="text-[10px] leading-tight" style={{ color: 'var(--lux-text-muted-3)' }}>/ mese</p>
                          <p className="text-[10px] leading-tight" style={{ color: 'var(--lux-text-muted-3)' }}>equiv. annuale</p>
                        </div>
                      </div>
                      <p className="text-xs mt-2" style={{ color: 'var(--lux-text-muted-3)' }}>
                        €{plan.priceAnnual.toLocaleString('it-IT')} {t.pricing_annual_label}
                      </p>
                    </div>

                    {/* ④ Included features */}
                    <div className="mt-6 space-y-3">
                      {feats.map((f, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                            style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.25)' }}>
                            <Check size={9} style={{ color: 'var(--lux-gold)' }} strokeWidth={2.5} />
                          </div>
                          <span className="text-[13px] leading-snug" style={{ color: 'var(--lux-text-sec)' }}>{f}</span>
                        </div>
                      ))}
                    </div>

                    {/* ⑤ Excluded features — dash style, no line-through */}
                    {missing.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {missing.map((f, i) => (
                          <div key={i} className="flex items-start gap-2.5">
                            <span className="shrink-0 mt-0.5 text-[13px] select-none font-light" style={{ color: 'var(--lux-text-muted-3)' }}>—</span>
                            <span className="text-[12px] leading-snug" style={{ color: 'var(--lux-text-muted-3)' }}>{f}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ⑥ CTA — with explicit border-top separator */}
                    <div className="mt-7 pt-6" style={{ borderTop: '1px solid var(--lux-border-1)' }}>
                      <button onClick={() => scrollTo('demo')}
                        className="w-full py-4 text-xs font-semibold tracking-widest uppercase transition-all"
                        style={{
                          background: plan.popular ? 'linear-gradient(135deg, #f3e3b6 0%, #c9a84c 45%, #a37e2c 100%)' : 'transparent',
                          color: plan.popular ? '#000' : 'var(--lux-gold)',
                          border: '1px solid var(--lux-gold)',
                          transition: 'background 0.2s ease, color 0.2s ease',
                        }}
                        onMouseEnter={e => {
                          if (!plan.popular) {
                            e.currentTarget.style.background = 'rgba(201,168,76,0.08)';
                          }
                        }}
                        onMouseLeave={e => {
                          if (!plan.popular) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}>
                        {t.pricing_cta}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mt-10">
            <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
              {lang === 'it' ? 'Licenza Lifetime disponibile su richiesta.' : 'Lifetime licence available on request.'}
              {' '}
              <a href="mailto:info@shangrilux.com" className="underline-offset-2 underline" style={{ color: 'var(--lux-text-muted-2)' }}>
                {lang === 'it' ? 'Scrivici' : 'Contact us'}
              </a>
            </p>
            <span className="hidden sm:block text-xs" style={{ color: 'var(--lux-border-3)' }}>·</span>
            <a href="/pricing" data-testid="pricing-full-link"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest transition-opacity hover:opacity-70"
              style={{ color: 'var(--lux-gold)' }}>
              {lang === 'it' ? 'Confronto completo' : 'Full comparison'}
              <ChevronRight size={11} />
            </a>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────── */}
      <section id="faq" className="py-20 md:py-28 px-5 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <div className="reveal text-center mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.35em] mb-5" style={{ color: 'var(--lux-gold)' }}>{t.faq_label}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light" style={{ color: 'var(--lux-text)' }}>{t.faq_title}</h2>
          </div>
          <div style={{ borderTop: '1px solid var(--lux-border-1)' }}>
            {FAQS.map((q, idx) => {
              const isOpen = openFaq === q;
              return (
                <div key={q} style={{ borderBottom: '1px solid var(--lux-border-1)' }}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : q)}
                    className="w-full py-5 sm:py-6 flex items-start justify-between text-left gap-5 group"
                    aria-expanded={isOpen}>
                    <div className="flex items-start gap-4">
                      <span className="text-[10px] font-mono mt-0.5 shrink-0 tabular-nums"
                        style={{ color: 'var(--lux-text-muted-3)', minWidth: '1.5rem' }}>
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="text-sm sm:text-[15px] font-medium leading-snug"
                        style={{ color: isOpen ? 'var(--lux-text)' : 'var(--lux-text-sec)', transition: 'color 0.2s' }}>
                        {t[q]}
                      </span>
                    </div>
                    <div className="shrink-0 mt-0.5">
                      <div className={`faq-chevron${isOpen ? ' open' : ''}`}>
                        <ChevronDown size={16} style={{ color: isOpen ? 'var(--lux-gold)' : 'var(--lux-text-muted-3)' }} />
                      </div>
                    </div>
                  </button>
                  <div className={`faq-answer${isOpen ? ' open' : ''}`}>
                    <div className="pb-6 pl-10 pr-6">
                      <p className="text-sm leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>
                        {t[`a${q.slice(1)}`]}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
              {lang === 'it' ? 'Hai altre domande?' : 'Have more questions?'}{' '}
              <a href="mailto:info@shangrilux.com" className="underline underline-offset-2" style={{ color: 'var(--lux-text-muted-2)' }}>
                {lang === 'it' ? 'Scrivici su info@shangrilux.com' : 'Email us at info@shangrilux.com'}
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* ── DEMO FORM ───────────────────────────────── */}
      <section id="demo" className="py-20 md:py-28 px-5 sm:px-6" style={{ background: 'var(--lux-surface-1)', borderTop: '1px solid var(--lux-border-1)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left: info */}
            <div>
              <p className="text-xs uppercase tracking-[0.3em] mb-5" style={{ color: 'var(--lux-gold)' }}>{t.demo_label}</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-light mb-5 leading-tight" style={{ color: 'var(--lux-text)' }}>
                {t.demo_title.split('\n').map((l, i) => <span key={i}>{l}{i < t.demo_title.split('\n').length - 1 && <br />}</span>)}
              </h2>
              <p className="text-sm leading-relaxed mb-8" style={{ color: 'var(--lux-text-muted)' }}>{t.demo_sub}</p>

              <div className="p-5 rounded-sm mb-8" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Clock size={13} style={{ color: 'var(--lux-gold)' }} />
                  <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--lux-gold)' }}>{t.demo_badge}</p>
                </div>
                <div className="space-y-3">
                  {t.demo_items.map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check size={12} className="shrink-0 mt-0.5" style={{ color: 'var(--lux-gold)' }} />
                      <span className="text-xs leading-relaxed" style={{ color: 'var(--lux-text-sec)' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-sm" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
                <Shield size={16} className="shrink-0 mt-0.5" style={{ color: 'var(--lux-text-muted-2)' }} strokeWidth={1.5} />
                <p className="text-xs leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>
                  {lang === 'it'
                    ? 'I tuoi dati non vengono condivisi con terze parti. L\'ambiente demo viene cancellato automaticamente dopo 24 ore.'
                    : 'Your data is not shared with third parties. The demo environment is automatically deleted after 24 hours.'}
                </p>
              </div>
            </div>

            {/* Right: form */}
            <div className="rounded-sm p-7 sm:p-8" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
              {!demoResult ? (
                <form onSubmit={submitDemo} className="space-y-5" noValidate>
                  <DInput label={t.demo_name} value={demoForm.name} onChange={v => setDf('name', v)} placeholder="Mario Rossi" required testId="demo-name" />
                  <DInput label={t.demo_company} value={demoForm.company} onChange={v => setDf('company', v)} placeholder="Villa Aurora" required testId="demo-company" />
                  <DInput label={t.demo_email} type="email" value={demoForm.email} onChange={v => setDf('email', v)} placeholder="mario@struttura.it" required testId="demo-email" />
                  <DInput label={t.demo_phone} type="tel" value={demoForm.phone} onChange={v => setDf('phone', v)} placeholder="+39 333 123 4567" testId="demo-phone" />

                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.18em] mb-2 font-medium" style={{ color: 'var(--lux-text-muted-2)' }}>{t.demo_plan}</label>
                    <select value={demoForm.plan} onChange={e => setDf('plan', e.target.value)}
                      className="landing-select"
                      data-testid="demo-plan-select">
                      {PLANS_DATA.map((plan, i) => (
                        <option key={plan.key} value={plan.key}>
                          {lang === 'it' ? T.it[`plan${i + 1}_name`] : T.en[`plan${i + 1}_name`]} — €{plan.priceMonthly}/mo
                        </option>
                      ))}
                    </select>
                  </div>

                  {demoError && (
                    <div className="flex items-start gap-2 p-3 rounded-sm" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                      <X size={13} className="shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
                      <p className="text-xs" style={{ color: '#f87171' }}>{demoError}</p>
                    </div>
                  )}

                  <button type="submit" disabled={demoLoading}
                    className="btn-metallic w-full py-4 text-sm font-semibold tracking-widest flex items-center justify-center gap-2 uppercase disabled:opacity-60"
                    data-testid="demo-submit-btn">
                    {demoLoading
                      ? <><Loader2 size={15} className="animate-spin" /> {t.demo_submitting}</>
                      : <>{t.demo_submit} <ArrowRight size={15} /></>}
                  </button>

                  <p className="text-[10px] text-center leading-relaxed" style={{ color: 'var(--lux-text-muted-3)' }}>
                    {t.demo_microcopy}
                  </p>
                </form>
              ) : (
                <DemoSuccess result={demoResult} t={t} lang={lang} copied={copied} onCopy={copyLink} onReset={() => setDemoResult(null)} />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────── */}
      <footer className="py-14 sm:py-16 px-5 sm:px-6" style={{ borderTop: '1px solid var(--lux-border-1)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="sm:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0" style={{ background: 'var(--lux-gold)' }}>
                  <Key size={13} color="#000" strokeWidth={2.5} />
                </div>
                <span className="font-serif text-xl font-light">Shangrilux</span>
              </div>
              <p className="text-sm leading-relaxed mb-5 max-w-xs" style={{ color: 'var(--lux-text-muted)' }}>{t.footer_tagline}</p>
              <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
                Design by{' '}
                <a href="https://francescorusso.work" target="_blank" rel="noreferrer"
                  className="underline hover:opacity-80 transition-opacity" style={{ color: 'var(--lux-text-muted-2)' }}>
                  Francesco Russo
                </a>
                {' '}·{' '}
                <a href="https://shangrilux.com" target="_blank" rel="noreferrer"
                  className="underline hover:opacity-80 transition-opacity" style={{ color: 'var(--lux-text-muted-2)' }}>
                  Shangrilux
                </a>
              </p>
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest mb-5" style={{ color: 'var(--lux-gold)' }}>
                {lang === 'it' ? 'Piattaforma' : 'Platform'}
              </p>
              <div className="space-y-3">
                {[
                  ['features',     t.nav_features],
                  ['for-who',      lang === 'it' ? 'Per chi è' : 'Who it\'s for'],
                  ['integrations', t.nav_integrations],
                  ['pricing',      t.nav_pricing],
                  ['faq',          'FAQ'],
                  ['demo',         'Demo'],
                ].map(([id, label]) => (
                  <button key={id} onClick={() => scrollTo(id)} className="block text-xs transition-opacity hover:opacity-70" style={{ color: 'var(--lux-text-muted)' }}>{label}</button>
                ))}
                <a href="/blog" className="block text-xs hover:opacity-70 transition-opacity" style={{ color: 'var(--lux-text-muted)' }}>
                  {lang === 'it' ? 'Guide & Blog' : 'Guides & Blog'}
                </a>
                <a href="/marketplace" className="block text-xs hover:opacity-70 transition-opacity" style={{ color: 'var(--lux-text-muted)' }}>
                  Marketplace
                </a>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest mb-5" style={{ color: 'var(--lux-gold)' }}>
                {lang === 'it' ? 'Sviluppatori' : 'Developers'}
              </p>
              <div className="space-y-3">
                <a href="/developers" className="block text-xs hover:opacity-70 transition-opacity" style={{ color: 'var(--lux-text-muted)' }}>
                  {lang === 'it' ? 'Documentazione API' : 'API Documentation'}
                </a>
                <a href="/developers#webhooks" className="block text-xs hover:opacity-70 transition-opacity" style={{ color: 'var(--lux-text-muted)' }}>
                  {lang === 'it' ? 'Webhook & HMAC' : 'Webhooks & HMAC'}
                </a>
                <a href="/developers#sdk" className="block text-xs hover:opacity-70 transition-opacity" style={{ color: 'var(--lux-text-muted)' }}>
                  {lang === 'it' ? 'SDK ufficiali' : 'Official SDKs'}
                </a>
                <a href="/marketplace" className="block text-xs hover:opacity-70 transition-opacity" style={{ color: 'var(--lux-text-muted)' }}>
                  {lang === 'it' ? 'Integrazioni certificate' : 'Certified Integrations'}
                </a>
                <a href="mailto:info@shangrilux.com" className="block text-xs hover:opacity-70 transition-opacity" style={{ color: 'var(--lux-text-muted)' }}>
                  info@shangrilux.com
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8" style={{ borderTop: '1px solid var(--lux-border-1)' }}>
            <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
              © {new Date().getFullYear()} Shangrilux. {t.footer_rights}
            </p>
            <div className="flex gap-6 flex-wrap">
              <a href="/privacy" className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'var(--lux-text-muted-3)' }}>{t.footer_privacy}</a>
              <a href="/cookie"  className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'var(--lux-text-muted-3)' }}>Cookie Policy</a>
              <a href="/terms"   className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'var(--lux-text-muted-3)' }}>{t.footer_terms}</a>
              <a href="/sitemap" className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'var(--lux-text-muted-3)' }}>Sitemap</a>
            </div>
          </div>
        </div>
      </footer>

      {showLogin && <LoginModal t={t} lang={lang} onClose={() => setShowLogin(false)} />}

      {/* ── STICKY MOBILE CTA ───────────────────────── */}
      <div
        className="block lg:hidden fixed bottom-0 left-0 right-0 z-40"
        style={{
          opacity: showStickyCTA && !stickyCTADismissed ? 1 : 0,
          pointerEvents: showStickyCTA && !stickyCTADismissed ? 'auto' : 'none',
          transform: showStickyCTA && !stickyCTADismissed ? 'translateY(0)' : 'translateY(100%)',
          transition: 'opacity 0.35s ease, transform 0.35s ease',
          paddingBottom: 'env(safe-area-inset-bottom)',
          willChange: 'transform, opacity',
        }}>
        <div className="h-8 w-full pointer-events-none" style={{ background: stickyGrad }} />
        <div className="px-4 pb-4 pt-0" style={{ background: stickyBg, backdropFilter: 'blur(20px)', borderTop: '1px solid rgba(201,168,76,0.2)' }}>
          <div className="flex items-center gap-3">
            <button onClick={() => scrollTo('demo')}
              className="btn-metallic flex-1 flex items-center justify-center gap-2 py-3.5 font-semibold text-sm tracking-wide uppercase"
              data-testid="sticky-cta-btn">
              {lang === 'it' ? 'Attiva Demo Gratuita' : 'Activate Free Demo'}
              <ChevronRight size={15} />
            </button>
            <button
              onClick={() => { setStickyCTADismissed(true); setShowStickyCTA(false); }}
              className="p-3 shrink-0 transition-opacity hover:opacity-70"
              style={{ color: 'var(--lux-text-muted)', border: '1px solid var(--lux-border-2)' }}
              aria-label="Chiudi"
              data-testid="sticky-cta-dismiss">
              <X size={16} />
            </button>
          </div>
          <p className="text-center text-[10px] mt-1.5" style={{ color: 'var(--lux-text-muted-3)' }}>
            {lang === 'it' ? 'Nessuna carta di credito · Dati cancellati in automatico' : 'No credit card · Data auto-deleted'}
          </p>
        </div>
      </div>
    </div>
  );
}
